import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type { DiscoverEventCard } from '../event-discovery/types';

/**
 * The organizer public profile's whole HTTP surface (`docs/standards/code-organization.md`).
 *
 * ## Three endpoints, and the split between them is the security boundary
 *
 * **Called with no session, and that must keep working.** `apiRequest`'s token resolver returns
 * `null` when nothing is stored, so no `Authorization` header is sent and the public routes are
 * reached anonymously — which is what `ORG-017` AC-3 and `MEM-018` AC-6 require, and what
 * `EDGE_PUBLIC_ROUTES` makes true at the gateway too. That applies to the profile and to the
 * event lists. It does **not** apply to the follow state, which is member data and is only asked
 * for when there is a member to ask about — see {@link useOrganizerFollow}.
 *
 * ## Why the profile and its events are two queries
 *
 * They are served by two domains: `organizer-profile` owns the organizer's own record and
 * `event-discovery` owns the narrowing of an event into a public row. Composing them here rather
 * than on the server keeps that ownership intact, and it is better on a phone besides — the
 * header band paints while the lists are still in flight, instead of the whole screen waiting on
 * the slower of two reads.
 */

export interface PublicOrganizerProfile {
  readonly slug: string;
  readonly organizerName: string;
  readonly basedIn: string;
  readonly organizationTypeLabel: string | null;
  /** `{based in} · {organization type}`, joined by the API so the preview and this cannot differ. */
  readonly metaLine: string;
  readonly introduction: string;
  /** Who hosts, as the organizer wrote it, or `null` while they have set none (`MEM-060` AC-3). */
  readonly hostLabel: string | null;
  readonly logoKey: string | null;
  /**
   * The logo, addressed by the API rather than derived here — the bucket is private and the
   * address expires, so it is minted on the read that returns this payload. `null` is a real
   * state: `ORG-015` AC-2 says a profile with no logo renders an initials tile.
   */
  readonly logoUrl: string | null;
  /**
   * The media gallery, shown beneath the introduction (`ORG-018` AC-1).
   *
   * Addressed on the server for the same reason the logo is, and because this reader has no
   * session: an Owner-guarded route is not something a signed-out visitor can ask for a URL.
   */
  readonly gallery: ReadonlyArray<{
    readonly objectId: string;
    readonly url: string;
    readonly expiresAt: string;
  }>;
  readonly website: string | null;
  readonly socialLinks: Readonly<Partial<Record<'linkedIn' | 'instagram' | 'facebook' | 'x', string>>>;
  readonly interestTags: ReadonlyArray<{ readonly termId: string; readonly displayName: string }>;
  readonly accessibilityCommitments: ReadonlyArray<{
    readonly termId: string;
    readonly displayName: string;
  }>;
  /** A count. Never a list of people — `ORG-017` AC-5, `MEM-018` AC-7, and the member data
   * boundary. */
  readonly upcomingEventCount: number;
  /** `MEM-018` AC-1's "Since 2024" — a year, because the year is the whole claim. */
  readonly hostingSinceYear: number;
}

/**
 * The two lists (`MEM-018` AC-2, AC-3).
 *
 * Rows are `DiscoverEventCard` — the same shape the Discover list serves, deliberately, so the
 * one vetted narrowing covers both surfaces. `apps/api/.../event-discovery/types.ts` carries the
 * argument, and the part that matters here is what is **absent**: no attendance figure, on any
 * row, in either list.
 */
export interface OrganizerEventLists {
  readonly upcoming: ReadonlyArray<DiscoverEventCard>;
  readonly past: ReadonlyArray<DiscoverEventCard>;
  readonly upcomingCount: number;
  readonly pastCount: number;
  /** Past event id → how many attended, only where five or more checked in (`MEM-060` AC-7). */
  readonly pastAttendedCounts: Readonly<Record<string, number>>;
  /** A sentence when the bounded read was a prefix, `null` when the lists are whole. */
  readonly incompleteReason: string | null;
}

/** `MEM-018` AC-5. `label` is the API's, not the screen's — the two strings are the AC. */
export interface OrganizerFollowState {
  readonly following: boolean;
  readonly label: string;
}

const profileKey = (slug: string): readonly unknown[] => ['public-organizer-profile', slug];
const eventsKey = (slug: string): readonly unknown[] => ['public-organizer-profile', slug, 'events'];
const followKey = (slug: string): readonly unknown[] => ['organizer-follow', slug];

export function usePublicOrganizerProfile(slug: string): UseQueryResult<PublicOrganizerProfile> {
  return useQuery({
    queryKey: profileKey(slug),
    queryFn: () =>
      apiRequest<PublicOrganizerProfile>(
        `/api/organizer-profile/public/${encodeURIComponent(slug)}`,
      ),
    retry: false,
  });
}

/**
 * The organizer's published, public events — upcoming and past (`MEM-018` AC-2, AC-3, AC-4).
 *
 * `enabled` exists for one caller: `SlugRoutePage` resolves the organizer before deciding whether
 * a CMS page answers the address instead, and asking for events at an address that turns out to
 * be a marketing page would be a request on the wire for every 404 anyone types.
 */
export function useOrganizerEvents(
  slug: string,
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<OrganizerEventLists> {
  return useQuery({
    queryKey: eventsKey(slug),
    queryFn: () =>
      apiRequest<OrganizerEventLists>(
        `/api/event-discovery/organizers/${encodeURIComponent(slug)}/events`,
      ),
    retry: false,
    enabled: options.enabled ?? true,
  });
}

/**
 * Whether the signed-in member follows this organizer (`MEM-018` AC-5).
 *
 * **`enabled` is not an optimisation here, it is the requirement.** AC-5 is about *a signed-in
 * member*; a signed-out visitor reads the profile without the control (AC-6), rather than being
 * prompted mid-scroll. Asking anyway would put a 401 on the wire for every anonymous visitor to
 * every organizer page — and the caller would then have to tell "not following" apart from "not
 * signed in", which is the distinction the whole control turns on.
 */
export function useOrganizerFollow(
  slug: string,
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<OrganizerFollowState> {
  return useQuery({
    queryKey: followKey(slug),
    queryFn: () =>
      apiRequest<OrganizerFollowState>(`/api/organizer-follows/${encodeURIComponent(slug)}`),
    retry: false,
    enabled: options.enabled ?? true,
  });
}

/**
 * Follow or unfollow, as one mutation taking the state the member asked for.
 *
 * One hook rather than two, matching `attendee-preview`'s meet-list toggle: a toggle has one
 * intent — *"make it this"* — and splitting it into `useFollow` and `useUnfollow` leaves the
 * button choosing which hook to call from state it has already rendered. The response is the
 * state the server settled on, written straight into the cache rather than refetched, so the
 * label a member reads after tapping is the server's answer and not an optimistic guess.
 */
export function useSetOrganizerFollow(
  slug: string,
): UseMutationResult<OrganizerFollowState, Error, { readonly following: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ following }) =>
      apiRequest<OrganizerFollowState>(`/api/organizer-follows/${encodeURIComponent(slug)}`, {
        method: following ? 'PUT' : 'DELETE',
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(followKey(slug), data);
    },
  });
}
